import React, { useCallback } from "react";

import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalBody,
  ModalFooter,
  HStack,
  Spacer,
} from "@chakra-ui/react";

import useViewer from "../../hooks/useViewer";

import useTranslations from "../../hooks/useTranslations";
import useNotification from "../../hooks/useNotification";
import CampaignForm from "./CampaignForm";

import { GetViewerDocument, useInsertCampaign } from "../../graphql/generated/definitions";

export type CampaignInput = {
  id: string;
  name: string;
  description: string;
  reminderGap: number;
  reminderEnabled: "true" | "false";
  companyId: string;
};

type CampaignCreationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (input: CampaignInput) => void;
  defaultValues?: Partial<CampaignInput>;
};

const CampaignCreationModal: React.FC<CampaignCreationModalProps> = ({
  isOpen,
  onClose,
  onSubmit: handleSubmit,
  defaultValues = {},
}) => {
  const t = useTranslations();

  const {
    company: { id: companyId, reminderGap, reminderEnabled },
  } = useViewer();

  const { notifySuccess, notifyError } = useNotification();
  const [insertCampaign, { loading }] = useInsertCampaign({
    refetchQueries: [{ query: GetViewerDocument }],
  });

  const onSubmit = useCallback(
    async (data: CampaignInput) => {
      const insertedCampaign = await insertCampaign({
        variables: {
          input: {
            id: data.id,
            companyId,
            name: data.name,
            reminderEnabled: data.reminderEnabled === "true",
            reminderGap: data.reminderGap,
            description: data.description,
          },
        },
      });

      if (insertedCampaign.errors)
        notifyError({
          title: t("campaign.notification.error.create.title") as string,
        });
      else {
        notifySuccess({
          title: t("campaign.notification.success.create.title") as string,
        });
        onClose();
        if (handleSubmit) {
          handleSubmit(data);
        }
      }
    },
    [companyId, onClose]
  );

  return (
    <Modal
      isOpen={isOpen}
      aria-label="Campaign creation form"
      onClose={onClose}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {defaultValues.id
            ? t("reviews.updateCampaign", { name: defaultValues.name })
            : t("reviews.createCampaign")}
        </ModalHeader>
        <ModalBody>
          <CampaignForm
            formId="campaign-creation-form"
            onSubmit={onSubmit}
            defaultValues={{
              companyId,
              reminderEnabled: reminderEnabled ? "true" : "false",
              reminderGap: reminderGap,
              ...defaultValues,
            }}
          />
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button variant="ghost" onClick={onClose}>
              {t("forms.actions.cancel")}
            </Button>
            <Spacer />
            <Button
              type="submit"
              form="campaign-creation-form"
              isLoading={loading}
              colorScheme="blue"
            >
              {t("forms.actions.save")}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CampaignCreationModal;
